import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VisionboardImage, VISIONBOARD_MAX_IMAGES } from '../types';
import {
  getVisionboardImages,
  addVisionboardImageFromFile,
  addVisionboardImageFromUrl,
  updateVisionboardCaption,
  updateVisionboardOrder,
  deleteVisionboardImage,
} from '../firebase/visionboard.service';
import { downsampleImage } from '../../../mediaEditor/imageProcessor';
import { VideoIcon, TimeIcon, Image as ImageIcon, Camera } from '../../../icons';

interface VisionboardCollageProps {
  goalId: string;
  ownerId: string;
  commitmentVideoUrl?: string; // Commitment video van intake - wordt als eerste item getoond
}

/**
 * VisionboardCollage - Visual inspiration board for goals
 * 
 * Features:
 * - Upload images or add via URL
 * - Drag & drop to reorder
 * - Captions per image
 * - Max 12 images
 */
const VisionboardCollage: React.FC<VisionboardCollageProps> = ({
  goalId,
  ownerId,
  commitmentVideoUrl,
}) => {
  const [images, setImages] = useState<VisionboardImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [editingCaption, setEditingCaption] = useState<string | null>(null);
  const [captionInput, setCaptionInput] = useState('');
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Commitment video state
  const [isVideoHovered, setIsVideoHovered] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const commitmentVideoRef = useRef<HTMLVideoElement>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load images
  useEffect(() => {
    const loadImages = async () => {
      setIsLoading(true);
      try {
        const data = await getVisionboardImages(ownerId, goalId);
        setImages(data);
      } catch (err) {
        console.error('Failed to load visionboard:', err);
        setError('Kon afbeeldingen niet laden');
      } finally {
        setIsLoading(false);
      }
    };
    loadImages();
  }, [goalId, ownerId]);

  // Handle file upload
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      for (const file of Array.from(files)) {
        // Check limit
        if (images.length >= VISIONBOARD_MAX_IMAGES) {
          setError(`Maximum ${VISIONBOARD_MAX_IMAGES} afbeeldingen bereikt`);
          break;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
          continue;
        }

        // Compress/resize image (max 800px for visionboard)
        const optimizedBlob = await downsampleImage(file, 800);
        
        // Upload
        await addVisionboardImageFromFile(ownerId, goalId, optimizedBlob);
      }

      // Reload images
      const data = await getVisionboardImages(ownerId, goalId);
      setImages(data);
    } catch (err: any) {
      console.error('Failed to upload:', err);
      setError(err.message || 'Upload mislukt');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle URL add
  const handleAddUrl = async () => {
    if (!urlInput.trim()) return;

    setIsUploading(true);
    setError(null);

    try {
      // Basic URL validation
      new URL(urlInput);
      
      await addVisionboardImageFromUrl(ownerId, goalId, urlInput.trim());
      
      // Reload images
      const data = await getVisionboardImages(ownerId, goalId);
      setImages(data);
      
      setUrlInput('');
      setShowUrlInput(false);
    } catch (err: any) {
      console.error('Failed to add URL:', err);
      setError(err.message || 'Ongeldige URL');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle caption edit
  const handleCaptionSave = async (imageId: string) => {
    try {
      await updateVisionboardCaption(imageId, captionInput);
      setImages(prev => prev.map(img => 
        img.id === imageId ? { ...img, caption: captionInput } : img
      ));
      setEditingCaption(null);
      setCaptionInput('');
    } catch (err) {
      console.error('Failed to save caption:', err);
    }
  };

  // Handle delete
  const handleDelete = async (image: VisionboardImage) => {
    if (!window.confirm('Afbeelding verwijderen?')) return;

    try {
      await deleteVisionboardImage(image);
      setImages(prev => prev.filter(img => img.id !== image.id));
    } catch (err) {
      console.error('Failed to delete:', err);
      setError('Verwijderen mislukt');
    }
  };

  // Drag & drop handlers
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }

    // Reorder locally
    const draggedIndex = images.findIndex(img => img.id === draggedId);
    const targetIndex = images.findIndex(img => img.id === targetId);
    
    const newImages = [...images];
    const [removed] = newImages.splice(draggedIndex, 1);
    newImages.splice(targetIndex, 0, removed);
    
    // Update order values
    const updatedImages = newImages.map((img, index) => ({
      ...img,
      order: index,
    }));
    
    setImages(updatedImages);
    setDraggedId(null);

    // Save to Firebase
    try {
      await updateVisionboardOrder(
        updatedImages.map(img => ({ id: img.id, order: img.order }))
      );
    } catch (err) {
      console.error('Failed to save order:', err);
    }
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  const canAddMore = images.length < VISIONBOARD_MAX_IMAGES;

  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm">
        Visionboard laden...
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg">
          {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-red-400 hover:text-red-600"
          >
            ✕
          </button>
        </div>
      )}

      {/* Image grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {/* Commitment Video - Eerste item in grid (indien aanwezig) */}
        {commitmentVideoUrl && (
          <div
            className={`
              group relative bg-gray-900 rounded-lg overflow-hidden
              transition-all duration-300 ease-out
              ${isVideoHovered 
                ? 'col-span-2 row-span-1 z-10 shadow-xl scale-105' 
                : 'col-span-1 row-span-1 hover:shadow-lg'
              }
            `}
            onMouseEnter={() => setIsVideoHovered(true)}
            onMouseLeave={() => {
              setIsVideoHovered(false);
              if (commitmentVideoRef.current) {
                commitmentVideoRef.current.pause();
                setIsVideoPlaying(false);
              }
            }}
          >
            {/* Video container - Square default, 16:9 on hover */}
            <div className={`
              relative overflow-hidden
              ${isVideoHovered ? 'aspect-video' : 'aspect-square'}
            `}>
              <video
                ref={commitmentVideoRef}
                src={commitmentVideoUrl}
                className="w-full h-full object-cover"
                playsInline
                loop
                muted={!isVideoHovered}
                onClick={() => {
                  if (commitmentVideoRef.current) {
                    if (isVideoPlaying) {
                      commitmentVideoRef.current.pause();
                    } else {
                      commitmentVideoRef.current.play();
                    }
                    setIsVideoPlaying(!isVideoPlaying);
                  }
                }}
              />
              
              {/* Play/Pause overlay */}
              {!isVideoPlaying && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity">
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              )}
              
              {/* Video badge */}
              <div className="absolute top-2 left-2 flex items-center gap-1 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                <VideoIcon size={14} className="text-white" />
                <span>Start intentie</span>
              </div>
            </div>

            {/* Caption - niet bewerkbaar */}
            <div className="p-2 bg-white border-t">
              <p className="text-xs text-gray-600 truncate">
                Mijn commitment video
              </p>
            </div>
            
            {/* Geen delete button - commitment video is gepind */}
          </div>
        )}

        {images.map((image) => (
          <div
            key={image.id}
            draggable
            onDragStart={(e) => handleDragStart(e, image.id)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, image.id)}
            onDragEnd={handleDragEnd}
            className={`
              group relative bg-gray-100 rounded-lg overflow-hidden cursor-move
              transition-all duration-200
              ${draggedId === image.id ? 'opacity-50 scale-95' : 'hover:shadow-lg'}
            `}
          >
            {/* Image */}
            <div className="aspect-square">
              <img
                src={image.imageUrl}
                alt={image.caption || 'Visionboard afbeelding'}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%239ca3af" font-size="14">📷</text></svg>';
                }}
              />
            </div>

            {/* Caption */}
            <div className="p-2 bg-white border-t">
              {editingCaption === image.id ? (
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={captionInput}
                    onChange={(e) => setCaptionInput(e.target.value)}
                    placeholder="Beschrijving..."
                    className="flex-1 text-xs border rounded px-2 py-1"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCaptionSave(image.id);
                      if (e.key === 'Escape') setEditingCaption(null);
                    }}
                  />
                  <button
                    onClick={() => handleCaptionSave(image.id)}
                    className="text-xs text-green-600 hover:text-green-700 px-1"
                  >
                    ✓
                  </button>
                </div>
              ) : (
                <p
                  onClick={() => {
                    setEditingCaption(image.id);
                    setCaptionInput(image.caption || '');
                  }}
                  className="text-xs text-gray-600 truncate cursor-text hover:text-gray-900"
                  title="Klik om te bewerken"
                >
                  {image.caption || <span className="text-gray-400 italic">+ Beschrijving</span>}
                </p>
              )}
            </div>

            {/* Delete button (hover) */}
            <button
              onClick={() => handleDelete(image)}
              className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-xs hover:bg-red-600"
              title="Verwijderen"
            >
              ✕
            </button>

            {/* Drag handle indicator */}
            <div className="absolute top-2 left-2 text-white text-xs opacity-0 group-hover:opacity-70 transition-opacity">
              ⋮⋮
            </div>
          </div>
        ))}

        {/* Add button */}
        {canAddMore && (
          <div className="aspect-square bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-orange-400 hover:bg-orange-50 transition-colors">
            {showUrlInput ? (
              <div className="p-3 w-full">
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://..."
                  className="w-full text-xs border rounded px-2 py-1.5 mb-2"
                  autoFocus
                />
                <div className="flex gap-1">
                  <button
                    onClick={handleAddUrl}
                    disabled={isUploading}
                    className="flex-1 text-xs bg-orange-500 text-white rounded py-1 hover:bg-orange-600 disabled:opacity-50"
                  >
                    {isUploading ? '...' : 'Toevoegen'}
                  </button>
                  <button
                    onClick={() => {
                      setShowUrlInput(false);
                      setUrlInput('');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 px-2"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="text-gray-400 hover:text-orange-500 transition-colors mb-2"
                >
                  {isUploading ? <TimeIcon size={28} className="text-gray-400" /> : <Camera size={28} className="text-gray-400 hover:text-orange-500 transition-colors" />}
                </button>
                <span className="text-xs text-gray-500 mb-1">Upload afbeelding</span>
                <button
                  onClick={() => setShowUrlInput(true)}
                  className="text-xs text-orange-500 hover:text-orange-600"
                >
                  of plak URL
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Counter */}
      <div className="mt-4 text-center text-xs text-gray-400">
        {images.length}/{VISIONBOARD_MAX_IMAGES} afbeeldingen
        {!canAddMore && <span className="text-orange-500 ml-2">(maximum bereikt)</span>}
      </div>

      {/* Empty state - alleen tonen als er ook geen commitment video is */}
      {images.length === 0 && !commitmentVideoUrl && !isLoading && (
        <div className="text-center py-8 text-gray-400">
          <ImageIcon size={32} className="text-gray-400 mb-2 mx-auto block" />
          <p className="text-sm">Voeg inspirerende afbeeldingen toe aan je visionboard</p>
        </div>
      )}
    </div>
  );
};

export default VisionboardCollage;
